import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Clock, Pill, User, Stethoscope, Calendar, Hash } from 'lucide-react';
import api from '@/lib/axios';

interface PublicReceta {
  folio: string;
  fecha: string;
  status: string;
  vigente: boolean;
  indicaciones: string | null;
  notas: string | null;
  paciente: { nombre: string };
  clinica: { claveClues: string | null } | null;
  medico: { nombre: string; cedula: string | null; institucionFormadora: string | null; especialidad: string | null } | null;
  medicaciones: {
    denominacionGenerica: string;
    denominacionDistintiva: string | null;
    viaAdministracion: string | null;
    dosis: string | null;
    frecuencia: string | null;
    duracion: string | null;
    indicaciones: string | null;
  }[];
}

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function RecetaDigitalPage() {
  const { folio } = useParams<{ folio: string }>();

  const { data, isLoading, isError } = useQuery<PublicReceta>({
    queryKey: ['receta-publica', folio],
    queryFn: () => api.get(`/api/public/recetas/${folio}`).then(r => r.data),
    retry: false,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
            <Pill size={16} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400 leading-none">Verificación de</p>
            <p className="text-sm font-semibold text-gray-800 leading-tight">Receta Digital</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {isLoading && (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Verificando receta...</p>
          </div>
        )}

        {isError && (
          <div className="text-center py-20">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-1">Receta no encontrada</p>
            <p className="text-sm text-gray-500">El folio <strong>{folio}</strong> no corresponde a ninguna receta registrada.</p>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Vigencia badge */}
            <div className={`rounded-2xl p-5 flex items-center gap-4 ${
              data.vigente
                ? 'bg-green-50 border border-green-200'
                : data.status === 'cancelada'
                  ? 'bg-gray-50 border border-gray-200'
                  : 'bg-amber-50 border border-amber-200'
            }`}>
              {data.vigente ? (
                <CheckCircle2 size={40} className="text-green-500 flex-shrink-0" />
              ) : data.status === 'cancelada' ? (
                <XCircle size={40} className="text-gray-400 flex-shrink-0" />
              ) : (
                <Clock size={40} className="text-amber-500 flex-shrink-0" />
              )}
              <div>
                <p className={`text-xl font-bold ${
                  data.vigente ? 'text-green-700' : data.status === 'cancelada' ? 'text-gray-500' : 'text-amber-700'
                }`}>
                  {data.vigente ? 'Receta Vigente' : data.status === 'cancelada' ? 'Receta Cancelada' : 'Receta Vencida'}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {data.vigente
                    ? 'Esta receta es válida y se encuentra activa.'
                    : data.status === 'cancelada'
                      ? 'Esta receta fue cancelada y no tiene validez.'
                      : 'Existe una receta más reciente activa para este paciente.'}
                </p>
              </div>
            </div>

            {/* Folio + fecha */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Hash size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Folio</p>
                  <p className="text-sm font-semibold text-gray-800 font-mono">{data.folio}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Fecha de emisión</p>
                  <p className="text-sm font-medium text-gray-700">{formatDate(data.fecha)}</p>
                </div>
              </div>
            </div>

            {/* Médico */}
            {data.medico && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Stethoscope size={15} className="text-primary-500" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Médico</p>
                </div>
                <p className="text-base font-semibold text-gray-800">{data.medico.nombre}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {data.medico.cedula && (
                    <p className="text-xs text-gray-500">Cédula: <span className="font-medium text-gray-700">{data.medico.cedula}</span></p>
                  )}
                  {data.medico.especialidad && (
                    <p className="text-xs text-gray-500">{data.medico.especialidad}</p>
                  )}
                </div>
                {data.medico.institucionFormadora && (
                  <p className="text-xs text-gray-400 mt-1">Institución formadora: {data.medico.institucionFormadora}</p>
                )}
                {data.clinica?.claveClues && (
                  <p className="text-xs text-gray-400 mt-0.5">CLUES: {data.clinica.claveClues}</p>
                )}
              </div>
            )}

            {/* Paciente */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <User size={15} className="text-primary-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paciente</p>
              </div>
              <p className="text-base font-semibold text-gray-800">{data.paciente.nombre}</p>
              <p className="text-xs text-gray-400 mt-0.5">Nombre parcialmente oculto por privacidad</p>
            </div>

            {/* Indicación */}
            {data.indicaciones && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Diagnóstico / Indicación</p>
                <p className="text-sm text-gray-700">{data.indicaciones}</p>
              </div>
            )}

            {/* Medicamentos */}
            {data.medicaciones.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Pill size={15} className="text-primary-500" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Medicamentos ({data.medicaciones.length})
                  </p>
                </div>
                <div className="space-y-3">
                  {data.medicaciones.map((m, i) => (
                    <div key={i} className="flex gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                      <span className="w-6 h-6 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">
                          {m.denominacionGenerica}
                          {m.denominacionDistintiva && <span className="font-normal text-gray-500"> ({m.denominacionDistintiva})</span>}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {m.dosis             && <span className="text-xs text-gray-500">{m.dosis}</span>}
                          {m.frecuencia        && <span className="text-xs text-gray-500">{m.frecuencia}</span>}
                          {m.duracion          && <span className="text-xs text-gray-500">por {m.duracion}</span>}
                          {m.viaAdministracion && <span className="text-xs text-gray-500">vía {m.viaAdministracion}</span>}
                        </div>
                        {m.indicaciones && (
                          <p className="text-xs text-gray-400 italic mt-0.5">{m.indicaciones}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notas */}
            {data.notas && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Instrucciones generales</p>
                <p className="text-sm text-gray-700">{data.notas}</p>
              </div>
            )}

            {/* Footer */}
            <p className="text-center text-xs text-gray-400 py-4">
              Documento generado digitalmente · Sistema de Gestión Clínica
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
